"use client";

/**
 * 路由層權限守衛（WP-A3，B2 批）。
 *
 * 改版前 admin 的唯一閘門是 AdminShell 的選單過濾——**純顯示層**：選單裡看不到的
 * 頁面，把網址直接貼進網址列照樣進得去，頁面骨架照畫、請求照發，使用者看到的是
 * 一整頁各自為政的錯誤訊息。後端的 fail-closed 守衛（api/app/core/route_permissions.py）
 * 會把資料擋下來，但「擋在哪一層、使用者看到什麼」是錯的：舊站 AccessControl.php
 * 在**進入 action 之前**就丟 ForbiddenHttpException，使用者看到的是一頁 403，
 * 不是一頁壞掉的表格。這支元件把那個語意搬回來。
 *
 * 接法：AdminShell 用 <PermissionProvider> 把 GET /me 的結果供給整棵 (dashboard)
 * 子樹，並把 children 包在 <RequirePermission> 裡。因此**所有**後台頁面自動受保護，
 * 不需要（也不應該）每頁自己記得加一行——「120 頁裡有一頁忘記標」正是後端
 * route_permissions.py 開頭記載的那個坑。需要更細的節點時，頁面內可再包一層
 * <RequirePermission node="...">。
 *
 * ⛔ fail closed：權限資料取不到就是沒有權限（見 lib/permissions.ts）。
 *    401 這裡什麼都不做——api-client 收到 401 會先 clearToken() 再觸發
 *    onUnauthorized，token 沒了就落回 pending（不畫任何東西），導頁與訊息
 *    交給 lib/sessionExpiry.ts 與登入頁。
 */

import { createContext, useContext, useMemo, useSyncExternalStore, type ReactNode } from "react";
import { usePathname, useRouter } from "next/navigation";
import { Alert, Button, PageHeader } from "@ccheng/ui";
import { getToken } from "../lib/apiClient";
import { adminNav } from "../nav/menu";
import {
  can,
  canAny,
  decideAccess,
  requiredNodesForPath,
  type PermissionSubject,
} from "../lib/permissions";

interface PermissionContextValue {
  subject: PermissionSubject | undefined;
  /** 權限資料還在路上（尚未 mount／尚未取得／無 token）。 */
  pending: boolean;
}

/**
 * 預設值刻意是「pending 且沒有身分」而不是 undefined：即使有人把受保護的內容放到
 * Provider 之外，也只會什麼都不畫，不會意外放行。
 *
 * 對外匯出只為兩件事：驗收時可直接注入身分渲染守衛（admin 沒有 test runner），
 * 以及未來若有非 AdminShell 的殼層要自行供給身分。一般情況一律用 PermissionProvider。
 */
export const PermissionContext = createContext<PermissionContextValue>({
  subject: undefined,
  pending: true,
});

export interface PermissionProviderProps {
  /** GET /api/v1/me 的回應（AdminShell 已經取過，不重複發請求）。 */
  subject: PermissionSubject | undefined;
  /** useCurrentUser().loading。 */
  loading: boolean;
  children: ReactNode;
}

/** useSyncExternalStore 的空訂閱：值永遠不變，只靠 server/client 快照的差異表達「是否已在瀏覽器」。 */
const NEVER_CHANGES = () => () => {};
const ON_CLIENT = () => true;
const ON_SERVER = () => false;

/**
 * 供給整棵子樹的權限來源。
 *
 * mounted 的用途不是視覺，是**正確性**：token 存在 localStorage，伺服端渲染拿不到，
 * 若第一次客戶端渲染就直接判定，SSR 與 hydration 的輸出會不一致（React 會整棵重畫
 * 並在 console 報錯）。用 useSyncExternalStore 的 server/client 快照差異取得這個旗標
 * ——effect 裡 setState 會被 react-hooks/set-state-in-effect 擋下，且那本來就會多一次
 * 串聯渲染。SSR 與 hydration 首次渲染一律 pending，之後才開始判斷；這也順帶讓授權
 * 使用者不會先看到 403 閃一下。
 */
export function PermissionProvider({ subject, loading, children }: PermissionProviderProps) {
  const mounted = useSyncExternalStore(NEVER_CHANGES, ON_CLIENT, ON_SERVER);

  const value = useMemo<PermissionContextValue>(
    () => ({
      subject,
      // 無 token＝尚未登入（AdminShell 的 effect 正在導回 /login）：這時畫 403 是
      // 錯的語意（舊站 AccessControl::denyAccess() 對 guest 是 loginRequired()，
      // 只有已登入者才丟 403），故一律歸類為 pending。
      pending: !mounted || loading || !getToken(),
    }),
    [subject, loading, mounted],
  );

  return <PermissionContext.Provider value={value}>{children}</PermissionContext.Provider>;
}

export interface UsePermissionsResult {
  /** can("/admin/user/index") —— 頁面內判斷單一節點。 */
  can: (node: string) => boolean;
  /** canAny(["/admin/email-template/index", "/admin/line-template/index"]) —— 持任一即可。 */
  canAny: (nodes: readonly string[]) => boolean;
  /** false＝權限資料還沒到位，此時 can() 一律回 false（fail closed）。 */
  ready: boolean;
}

/**
 * 頁面內用的權限判斷。刻意只回傳綁好身分的 can/canAny，不外流權限閉包本身——
 * 呼叫端拿不到陣列就沒辦法在別處長出第二套判斷邏輯。
 */
export function usePermissions(): UsePermissionsResult {
  const { subject, pending } = useContext(PermissionContext);
  return useMemo(
    () => ({
      can: (node: string) => !pending && can(subject, node),
      canAny: (nodes: readonly string[]) => !pending && canAny(subject, nodes),
      ready: !pending,
    }),
    [subject, pending],
  );
}

/**
 * 403 頁。
 *
 * 文案來源（migration/docs/用語對照表.md 逐字比對）：
 * - 標題「Forbidden」：舊站 admin/views/site/error.php 的 <h1>＝Yii
 *   ForbiddenHttpException 的 name。用語對照表無 zh-TW 對應（該表 5,987 條原文只有
 *   42.1% 有翻譯），照搬原文，不自行翻譯（業主裁決：不新增也不推測）。
 * - 內文「您沒有執行此操作的權限。」：舊站丟出的訊息是
 *   Yii::t('yii', 'You are not allowed to perform this action.')，該原文在用語對照表
 *   第 2214 條、zh-TW 欄為空，實際顯示值取自舊站實際載入的翻譯檔
 *   vendor/yiisoft/yii2/messages/zh-TW/yii.php:69，一字不差。
 * - 按鈕「回到首頁」：用語對照表第 954 條 "Back to Homepage" → 回到首頁。
 *
 * 版面用 @ccheng/ui 既有元件（PageHeader + Alert + Button），沒有自刻的顏色或字重。
 */
export function Forbidden() {
  const router = useRouter();
  return (
    <>
      <PageHeader title="Forbidden" />
      <Alert
        variant="danger"
        role="alert"
        action={
          <Button variant="secondary" onClick={() => router.replace("/")}>
            回到首頁
          </Button>
        }
      >
        您沒有執行此操作的權限。
      </Alert>
    </>
  );
}

export interface RequirePermissionProps {
  /** 指定節點；不給則由目前路徑對 nav/menu.ts 反查（AdminShell 的用法）。 */
  node?: string;
  /** 指定多個節點，any-of。與 node 併用時取聯集。 */
  nodes?: readonly string[];
  children: ReactNode;
}

/**
 * 守衛本體：pending 什麼都不畫、denied 畫 403、granted 才畫 children。
 *
 * 「pending 什麼都不畫」也是 fail closed 的一部分——被保護的內容在權限確定之前
 * 一次都不會出現在 DOM 裡。代價是 (dashboard) 的伺服端輸出不再包含頁面內容
 * （選單與外框照舊），拿 curl 驗收頁面**內容**的作法要改用瀏覽器。
 */
export function RequirePermission({ node, nodes, children }: RequirePermissionProps) {
  const { subject, pending } = useContext(PermissionContext);
  const pathname = usePathname();

  const required = useMemo(() => {
    if (node || nodes) {
      return Array.from(new Set([...(node ? [node] : []), ...(nodes ?? [])]));
    }
    return requiredNodesForPath(adminNav, pathname);
  }, [node, nodes, pathname]);

  const decision = decideAccess({ subject, pending, nodes: required });
  if (decision === "pending") return null;
  if (decision === "denied") return <Forbidden />;
  return <>{children}</>;
}
